
import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, type PanInfo } from "framer-motion";

export type SnapState = "peek" | "half" | "full";

interface BottomSheetProps {
  snap: SnapState;
  onSnapChange: (s: SnapState) => void;
  peek: ReactNode; // shown in the handle row when peeking (e.g. "N homes in view")
  children: ReactNode;
}

const PEEK_PX = 96;

// A swipeable bottom sheet positioned ABSOLUTELY inside the map section (not
// page-fixed) so it never hijacks the whole scrolling page. Three snap states:
// peek / half / full. Drag the handle to move between them.
export default function BottomSheet({
  snap,
  onSnapChange,
  peek,
  children,
}: BottomSheetProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerH, setContainerH] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerH(el.clientHeight));
    ro.observe(el);
    setContainerH(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  const heights: Record<SnapState, number> = {
    peek: PEEK_PX,
    half: containerH ? Math.round(containerH * 0.5) : PEEK_PX,
    full: containerH ? Math.round(containerH * 0.92) : PEEK_PX,
  };

  const onDragEnd = (_e: unknown, info: PanInfo) => {
    const dy = info.offset.y;
    const vy = info.velocity.y;
    if (dy < -40 || vy < -400) {
      onSnapChange(snap === "peek" ? "half" : "full");
    } else if (dy > 40 || vy > 400) {
      onSnapChange(snap === "full" ? "half" : "peek");
    }
  };

  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute inset-0 z-[1000] lg:hidden"
    >
      <motion.div
        className="pointer-events-auto absolute inset-x-0 bottom-0 flex flex-col rounded-t-3xl border-t border-[var(--sand)] bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.18)]"
        animate={{ height: heights[snap] }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
      >
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.15}
          onDragEnd={onDragEnd}
          onClick={() => onSnapChange(snap === "peek" ? "half" : "peek")}
          className="flex flex-shrink-0 cursor-grab touch-none flex-col items-center pt-2.5 active:cursor-grabbing"
        >
          <div className="h-1.5 w-10 rounded-full bg-[var(--dune)]" />
          <div className="w-full px-4 py-2">{peek}</div>
        </motion.div>
        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      </motion.div>
    </div>
  );
}
